/* biome-ignore-all lint/complexity/noForEach: Dawn 標準コードは挙動維持のため変更しない */
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      /*
       * AUREME の色 / サイズ / 素材選択は HTML 再取得をまたぐため、
       * fetch 完了まで選択意図とメディア同期オプションを一時保持する。
       */
      pendingProductInfoState = {
        requestUrl: null,
        materialMode: null,
        sizeSelectionMode: null,
        requestedSizeValue: null,
        mediaActivationOptions: undefined,
        colorMediaId: undefined,
      };
      mediaSync = undefined;
      colorCardFieldsetSync = undefined;
      variantSelectionIntent = undefined;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.mediaSync = new window.AuremeProductInfo.MediaSync(this);
        this.colorCardFieldsetSync = new window.AuremeProductInfo.ColorCardFieldsetSync(this);
        this.variantSelectionIntent = new window.AuremeProductInfo.VariantSelectionIntent(this);
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.syncInitialVariantUrl();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          html.querySelectorAll('.scroll-trigger').forEach((element) => {
            element.classList.add('scroll-trigger--cancel');
          });
        });
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const selectionIntent = this.variantSelectionIntent.build({
          eventTarget: event.target,
          selectedOptionTarget: target,
        });

        this.resetProductFormState();
        if (selectionIntent.shouldCancelCounterLock) {
          this.cancelViewerCounterLock();
        }

        const { productUrl, sizeSelectionMode, materialMode } = selectionIntent;
        this.setPendingProductInfoState({
          requestUrl: productUrl,
          materialMode,
          sizeSelectionMode,
          requestedSizeValue: this.currentSizeValue,
          mediaActivationOptions: selectionIntent.mediaActivationOptions,
          colorMediaId: selectionIntent.colorMediaId,
        });
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            sizeSelectionMode,
            materialMode,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.removeSectionModals();

          const selector = this.getProductInfoSelector(updateFullPage);
          const nextProductInfo =
            html.querySelector(selector) || html.querySelector('product-info');
          const variant = this.getSelectedVariant(nextProductInfo);
          const preferredMediaId = this.consumePendingColorMediaId();
          const mediaActivationOptions = this.consumePendingMediaActivationOptions();
          this.applyResolvedSizeSelectionMode(nextProductInfo);
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.mediaSync.syncSwappedProductMedia({
            selector,
            preferredMediaId,
            fallbackMediaId: variant?.featured_media?.id,
            mediaActivationOptions,
          });
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.fetchProductInfoHtml(requestUrl)
          .then((html) => {
            callback(html);
            this.clearPendingSelectionState();
          })
          .then(() => {
            /* 最後にクリックしたオプション値にフォーカスを戻す */
            document.getElementById(targetId)?.focus({ preventScroll: true });
          })
          .catch((error) => this.handleProductInfoFetchError(error));
      }

      fetchProductInfoHtml(requestUrl) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        return fetch(requestUrl, { signal: this.abortController.signal }).then((response) =>
          response.text().then((responseText) => {
            this.setPendingProductInfoState({ requestUrl: null });
            return new DOMParser().parseFromString(responseText, 'text/html');
          }),
        );
      }

      clearPendingSelectionState() {
        this.setPendingProductInfoState({
          materialMode: null,
          sizeSelectionMode: null,
          requestedSizeValue: null,
        });
      }

      handleProductInfoFetchError(error) {
        /* AbortErrorは意図的なキャンセルなので無視 */
        if (error.name === 'AbortError') return;

        this.clearPendingSelectionState();
        console.error(error);
        /* バリアント情報取得失敗をユーザーに通知 */
        window.AuremeToast?.show({
          message: '商品情報の取得に失敗しました。\nページを再読み込みしてお試しください。',
          type: 'error',
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          'variant-selects [data-selected-variant]',
        )?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      syncInitialVariantUrl() {
        if (this.dataset.updateUrl !== 'true') return;

        const currentUrl = new URL(window.location.href);
        if (!currentUrl.searchParams.has('variant')) return;

        const variant = this.getSelectedVariant(this);
        if (!variant?.id) return;

        if (currentUrl.searchParams.get('variant') === String(variant.id)) return;

        currentUrl.searchParams.set('variant', String(variant.id));

        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
        window.history.replaceState(
          {},
          '',
          `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
      }

      get currentSizeSelectionMode() {
        return (
          this.pendingProductInfoState.sizeSelectionMode ||
          this.dataset.auremeSizeSelectionMode ||
          'auto'
        );
      }

      get currentSizeValue() {
        return (
          this.variantSelectors?.querySelector('[data-aureme-size-native-select]')?.value || null
        );
      }

      getResolvedSizeSelectionMode(productInfoElement) {
        const requestedSizeValue =
          this.pendingProductInfoState.requestedSizeValue || this.currentSizeValue;
        const resolvedSizeValue =
          productInfoElement?.querySelector('[data-aureme-size-native-select]')?.value ||
          requestedSizeValue;
        let sizeSelectionMode =
          this.pendingProductInfoState.sizeSelectionMode ||
          productInfoElement?.dataset.auremeSizeSelectionMode ||
          this.dataset.auremeSizeSelectionMode ||
          'auto';

        /*
         * 手動サイズ指定でも、切り替え先に同じサイズが存在せず別サイズへ解決された場合は
         * その時点で手動指定を解除し、以後の色変更へフォールバック結果を持ち越さない。
         */
        if (
          sizeSelectionMode === 'manual' &&
          requestedSizeValue &&
          resolvedSizeValue &&
          requestedSizeValue !== resolvedSizeValue
        ) {
          sizeSelectionMode = 'auto';
        }

        return sizeSelectionMode;
      }

      applyResolvedSizeSelectionMode(productInfoElement) {
        if (!productInfoElement) return;

        productInfoElement.dataset.auremeSizeSelectionMode =
          this.getResolvedSizeSelectionMode(productInfoElement);
      }

      get currentMaterialMode() {
        return (
          this.pendingProductInfoState.materialMode || this.dataset.auremeMaterialMode || 'selected'
        );
      }

      cancelViewerCounterLock() {
        this.querySelector('media-gallery [id^="GalleryViewer"]')?.cancelCounterLock?.();
      }

      consumePendingMediaActivationOptions() {
        const options = this.pendingProductInfoState.mediaActivationOptions;
        this.setPendingProductInfoState({ mediaActivationOptions: undefined });
        return options;
      }

      consumePendingColorMediaId() {
        const mediaId = this.pendingProductInfoState.colorMediaId;
        this.setPendingProductInfoState({ colorMediaId: undefined });
        return mediaId;
      }

      setPendingProductInfoState(nextState) {
        this.pendingProductInfoState = {
          ...this.pendingProductInfoState,
          ...nextState,
        };
      }

      getSelectedColorInput(productInfoElement = this) {
        return productInfoElement?.querySelector(
          '.product-form__input--aureme-color .aureme-color-card__input:checked',
        );
      }

      getSelectedColorMediaId(productInfoElement = this) {
        const mediaId = String(
          this.getSelectedColorInput(productInfoElement)?.dataset?.auremeMediaId || '',
        ).trim();
        return mediaId || undefined;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
        sizeSelectionMode = 'auto',
        materialMode = 'selected',
      ) {
        const params = [];

        if (!shouldFetchFullPage) {
          params.push(`section_id=${this.sectionId}`);
        }
        params.push(`aureme_size_selection=${sizeSelectionMode}`);
        params.push(`aureme_material_mode=${encodeURIComponent(materialMode)}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (!variantSelects) return;

        if (this.colorCardFieldsetSync.syncVariantSelectors(variantSelects)) return;

        HTMLUpdateUtility.viewTransition(
          this.variantSelectors,
          variantSelects,
          this.preProcessHtmlCallbacks,
        );
      }

      syncMaterialMode(html) {
        const source = html.querySelector('product-info');
        const materialMode =
          this.pendingProductInfoState.materialMode ||
          source?.dataset.auremeMaterialMode ||
          this.dataset.auremeMaterialMode ||
          'selected';
        this.dataset.auremeMaterialMode = materialMode;
      }

      syncSizeSelectionMode(html) {
        const source = html.querySelector('product-info');
        this.dataset.auremeSizeSelectionMode = this.getResolvedSizeSelectionMode(source);
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const nextProductInfo = html.querySelector('product-info');
          const variant = this.getSelectedVariant(nextProductInfo || html);
          const mediaActivationOptions = this.consumePendingMediaActivationOptions();
          const pendingColorMediaId = this.consumePendingColorMediaId();

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.syncMaterialMode(html);
          this.syncSizeSelectionMode(html);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.syncUpdatedProductMedia({
            html,
            variant,
            pendingColorMediaId,
            mediaActivationOptions,
          });
          this.syncProductInfoSections(html);
          this.syncProductSubmitButton(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      syncUpdatedProductMedia({ html, variant, pendingColorMediaId, mediaActivationOptions }) {
        const selectedColorMediaId = this.getSelectedColorMediaId() || pendingColorMediaId;

        this.mediaSync.updateMedia(
          html,
          {
            preferredMediaId: selectedColorMediaId,
            fallbackMediaId: variant?.featured_media?.id,
          },
          mediaActivationOptions,
        );
      }

      syncProductInfoSections(html) {
        const shouldHideWhenSourceHidden = ({ classList }) => classList.contains('hidden');

        this.syncSectionContent(html, 'price');
        this.syncSectionContent(html, 'AuremePriceMeta');
        this.syncSectionContent(html, 'AuremeAccordionMaterial', shouldHideWhenSourceHidden);
        this.syncSectionContent(html, 'AuremeAccordionSize', shouldHideWhenSourceHidden);
        this.syncSectionContent(html, 'Sku', shouldHideWhenSourceHidden);
        this.syncSectionContent(html, 'Inventory', ({ innerText }) => innerText === '');
        this.syncSectionContent(html, 'InventoryNotice', shouldHideWhenSourceHidden);
        this.syncSectionContent(html, 'Volume');
        this.syncSectionContent(html, 'Price-Per-Item', shouldHideWhenSourceHidden);
        this.syncSectionContent(html, 'NotAvailableText', shouldHideWhenSourceHidden);

        this.updateQuantityRules(this.sectionId, html);
        this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
        this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
      }

      syncSectionContent(html, id, shouldHide = () => false) {
        const source = html.getElementById(`${id}-${this.sectionId}`);
        const destination = this.querySelector(`#${id}-${this.dataset.section}`);
        if (!source || !destination) return;

        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('hidden', shouldHide(source));
      }

      syncProductSubmitButton(html) {
        /* ボタンの disabled 状態とテキストをサーバーレスポンスから動的に取得 */
        const submitBtnHtml = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const isDisabled = submitBtnHtml?.hasAttribute('disabled') ?? true;
        const buttonText = isDisabled
          ? submitBtnHtml?.querySelector('span')?.textContent?.trim() ||
            window.variantStrings.soldOut
          : undefined;
        this.productForm?.toggleSubmitButton(isDisabled, buttonText);

        /* カート上限到達の data 属性を同期 */
        if (submitBtnHtml?.dataset.cartLimitReached === 'true') {
          this.productForm?.submitButton?.setAttribute('data-cart-limit-reached', 'true');
        } else {
          this.productForm?.submitButton?.removeAttribute('data-cart-limit-reached');
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.productForm?.submitButton?.removeAttribute('data-cart-limit-reached');

        /* 補助テキストを非表示にする */
        const notAvailableText = this.querySelector(`#NotAvailableText-${this.dataset.section}`);
        notAvailableText?.classList.add('hidden');

        /* 予約商品から利用不可状態へ切り替わった際に表示が残らないようにする */
        const deliveryDate = this.querySelector(`#AuremeDeliveryDate-${this.dataset.section}`);
        if (deliveryDate) {
          deliveryDate.hidden = true;
        }

        const selectors = [
          'price',
          'Inventory',
          'InventoryNotice',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => {
          classList.add('hidden');
        });
      }

      /* MediaSync 分離後も、外部参照互換のため product-info 側の公開メソッドを残す */
      updateMedia(html, mediaOptions = {}, mediaActivationOptions = undefined) {
        return this.mediaSync.updateMedia(html, mediaOptions, mediaActivationOptions);
      }

      resolvePreferredMediaId(mediaGalleryElement, mediaOptions = {}) {
        return this.mediaSync.resolvePreferredMediaId(mediaGalleryElement, mediaOptions);
      }

      syncSwappedProductMedia(mediaOptions = {}) {
        return this.mediaSync.syncSwappedProductMedia(mediaOptions);
      }

      getProductInfoSelector(updateFullPage = false) {
        if (updateFullPage) {
          return `#MainProduct-${this.sectionId}`;
        }

        return `product-info[data-section="${this.sectionId}"]`;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity, 10)
            : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min, 10) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max, 10) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step, 10) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (const attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                /* 参照先の視覚的非表示ラベル要素を更新 */
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      removeSectionModals() {
        if (!this.sectionId) return;

        /* body 直下へ退避した popup / media modal も商品切替前に破棄して旧DOM参照を残さない */
        for (const modal of document.querySelectorAll(
          `modal-dialog[data-section="${this.sectionId}"], product-modal[data-section="${this.sectionId}"]`,
        )) {
          if (modal.hasAttribute('open') && typeof modal.hide === 'function') {
            modal.hide();
          }
          modal.remove();
        }
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
